import React, { useState } from 'react';
import { Brain, Loader2, FlaskConical, Database, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CompatibilityTestBench } from '@/components/admin/ai/CompatibilityTestBench';
import { CompatibilityCacheManager } from '@/components/admin/ai/CompatibilityCacheManager';
import { CompatibilityLogsTab } from '@/components/admin/ai/CompatibilityLogsTab';

const BioValidationSection: React.FC = () => {
  const [bio, setBio] = useState('');
  const [result, setResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleValidate = async () => {
    if (!bio.trim()) {
      toast.error('Por favor, digite uma bio para validar');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('evaluate-bio', {
        body: { bio: bio.trim() }
      });

      if (error) {
        console.error('Error calling evaluate-bio:', error);
        toast.error('Erro ao validar bio: ' + error.message);
        return;
      }

      setResult(data);
      toast.success('Bio validada com sucesso!');
    } catch (error) {
      console.error('Error validating bio:', error);
      toast.error('Erro inesperado ao validar bio');
    } finally {
      setIsLoading(false);
    }
  };

  const clearResult = () => {
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Input da Bio
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/60">
              Digite a bio para validar:
            </label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Digite uma biografia para testar a validação da IA..."
              className="min-h-[120px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
              rows={5}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleValidate}
              disabled={isLoading || !bio.trim()}
              className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validando...
                </>
              ) : (
                'Validar Bio'
              )}
            </Button>

            {result && (
              <Button
                onClick={clearResult}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Limpar Resultado
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Resultado da Validação</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Quick Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="bg-green-500/20 border-green-500/30">
                  <CardContent className="p-3">
                    <div className="text-xs text-green-300">Bem Escrita</div>
                    <div className="text-lg font-bold text-white">
                      {result.isWellWritten ? 'Sim' : 'Não'}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-500/20 border-blue-500/30">
                  <CardContent className="p-3">
                    <div className="text-xs text-blue-300">Score</div>
                    <div className="text-lg font-bold text-white">
                      {result.score || 0}/100
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-500/20 border-purple-500/30">
                  <CardContent className="p-3">
                    <div className="text-xs text-purple-300">Tópicos</div>
                    <div className="text-lg font-bold text-white">
                      {result.topicsMentioned?.length || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-500/20 border-orange-500/30">
                  <CardContent className="p-3">
                    <div className="text-xs text-orange-300">Interesses</div>
                    <div className="text-lg font-bold text-white">
                      {result.categorizedInterests?.length || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card className={`${
                  result.validationSource === 'local_regex' ? 'bg-red-500/20 border-red-500/30' :
                  result.validationSource === 'local_length' ? 'bg-yellow-500/20 border-yellow-500/30' :
                  result.validationSource === 'openai' ? 'bg-emerald-500/20 border-emerald-500/30' :
                  'bg-amber-500/20 border-amber-500/30'
                }`}>
                  <CardContent className="p-3">
                    <div className={`text-xs ${
                      result.validationSource === 'local_regex' ? 'text-red-300' :
                      result.validationSource === 'local_length' ? 'text-yellow-300' :
                      result.validationSource === 'openai' ? 'text-emerald-300' :
                      'text-amber-300'
                    }`}>Fonte</div>
                    <div className="text-lg font-bold text-white">
                      {result.validationSource === 'local_regex' ? '🔴 Regex' :
                       result.validationSource === 'local_length' ? '🟡 Tamanho' :
                       result.validationSource === 'openai' ? '🟢 OpenAI' :
                       '🟠 Fallback'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Feedback */}
              {result.feedback && (
                <Card className="bg-yellow-500/20 border-yellow-500/30">
                  <CardContent className="p-4">
                    <div className="text-sm text-yellow-300 mb-2">Feedback</div>
                    <div className="text-white">{result.feedback}</div>
                  </CardContent>
                </Card>
              )}

              {/* Full JSON Result */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base">JSON Completo</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words bg-black/30 p-4 rounded-lg overflow-x-auto">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminAI = () => {
  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Brain className="h-8 w-8 text-luxury-pinkBright" />
        <h1 className="text-3xl font-bold text-white">Teste de IA</h1>
      </div>

      <Tabs defaultValue="bio" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1">
          <TabsTrigger
            value="bio"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 flex items-center gap-2"
          >
            <Brain className="h-4 w-4" />
            Validação de Bio
          </TabsTrigger>
          <TabsTrigger
            value="compatibility"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 flex items-center gap-2"
          >
            <FlaskConical className="h-4 w-4" />
            Teste de Compatibilidade
          </TabsTrigger>
          <TabsTrigger
            value="cache"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 flex items-center gap-2"
          >
            <Database className="h-4 w-4" />
            Cache
          </TabsTrigger>
          <TabsTrigger
            value="logs"
            className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Registros
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bio" className="mt-6">
          <BioValidationSection />
        </TabsContent>

        <TabsContent value="compatibility" className="mt-6">
          <CompatibilityTestBench />
        </TabsContent>

        <TabsContent value="cache" className="mt-6">
          <CompatibilityCacheManager />
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <CompatibilityLogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAI;
